import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Link, Outlet, NavLink } from "react-router-dom";
import Startpage from "@/pages/StartPage";
import TeamPage from "@/pages/TeamPage";
import JoinPage from "@/pages/JoinPage";
import HirePage from "@/pages/HirePage";
import PrivacyPage from "@/pages/PrivacyPage";
import Logo from "@/assets/images/Boldbrave.svg";
import BlockLogo from "@/assets/images/Boldbrave-block.svg";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ServicesPlatformPage from "./pages/ServicesPlatformPage";
import ScrollToTop from "./components/scrolltotop";
import ServicesWebappsPage from "./pages/ServicesWebappsPage";
import ServicesStartupsPage from "./pages/ServicesStartupsPage";
import ServicesAdvisoryChangePage from "./pages/ServicesAdvisoryChangePage";
import CTAButton from "./components/button";
import CVJohanRosengren from "./pages/CVJohanRosengren";
import CVMarieColetti from "./pages/CVMarieColetti";
import CVMagnusChristiansson from "./pages/CVMagnusChristiansson";
import CVPhilipPersson from "./pages/CVPhilipPersson";
import CVPatrikRizell from "./pages/CVPatrikRizell";
import CVViktorNilsson from "./pages/CVViktorNilsson";
import CVPatrikPochill from "./pages/CVPatrikPochill";
import CVLyupchoKotev from "./pages/CVLyupchoKotev";
import CVVincentLam from "./pages/CVVincentLam";
import CVPatrickFogwall from "./pages/CVPatrickFogwall";
import CVJohannesTegner from "./pages/CVJohannesTegner";
import CVAlexanderNyberg from "./pages/CVAlexanderNyberg";
import CVThomasBergstrom from "./pages/CVThomasBergstrom";
import CVMarkusRytterkull from "./pages/CVMarkusRytterkull";
import CVTomasBoremalm from "./pages/CVTomasBoremalm";
import CVNellLevasseur from "./pages/CVNellLevasseur";
import CVAndersSoderlin from "./pages/CVAndersSoderlin";
import CVArunRathi from "./pages/CVArunRathi";
import CVMartinLevin from "./pages/CVMartinLevin";
import CVSebastianAndersson from "./pages/CVSebastianAndersson";
import CVAntonAlfsson from "./pages/CVAntonAlfsson";
import CVFelixTran from "./pages/CVFelixTran";
import CVJoenMauritzon from "./pages/CVJoenMauritzon";
import CVMarcelUrrutiaNilsson from "./pages/CVMarcelUrrutiaNilsson";
import CVNilsBroman from "./pages/CVNilsBroman";
import CVWilliamPhan from "./pages/CVWilliamPhan";
import CVJerryHon from "./pages/CVJerryHon";
import CVVladislavsGaidass from "./pages/CVVladislavsGaidass";
import CVCarlEmilKjellstrand from "./pages/CVCarlEmilKjellstrand";
import CVAlexanderRaunert from "./pages/CVAlexanderRaunert";
import CVAlexanderMadsen from "./pages/CVAlexanderMadsen";
import CVIndexPage from "./pages/CVIndexPage";


const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

function NavItem({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `inline-flex items-center transition before:content-[''] before:inline-block before:h-1.5 before:w-1.5 before:rounded-full before:mr-2 before:align-middle before:bg-[#FF893F] ${
          isActive
            ? "before:opacity-100 text-white"
            : "before:opacity-0 text-white/70 hover:text-white"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function Layout() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div className="relative min-h-screen w-full bg-[#0c1110] text-white overflow-hidden">
      {/* Basbakgrund */}
      <div className="pointer-events-none absolute inset-0 bg-[#0c1110]" />

      {/* Radial */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
        <div className="w-[100vw] h-[100vh] bg-[radial-gradient(circle_at_top_center,_#1f3b37_0%,_#0c1110_60%)]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#0c1110]/95 md:bg-transparent md:static md:z-40 md:backdrop-blur">
        <Container>
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 py-2 md:py-4 pt-[max(12px,env(safe-area-inset-top))]">
            <Link
              to="/"
              className="flex items-center gap-2 font-extrabold tracking-tight"
              onClick={() => setOpen(false)}
            >
              <span className="inline-flex items-center origin-left scale-[0.75] md:scale-100">
                <Logo />
              </span>
            </Link>

            <nav className="hidden md:block justify-self-center">
              <ul className="flex gap-6 text-sm">
                <li>
                  <NavItem to="/services">Services</NavItem>
                </li>
                <li>
                  <NavItem to="/team">The team</NavItem>
                </li>
                <li>
                  <NavItem to="/join">Join us</NavItem>
                </li>
                <li>
                  <NavItem to="/about">About</NavItem>
                </li>
                <li>
                  <NavItem to="/hire">Contact</NavItem>
                </li>
              </ul>
            </nav>

            <div className="justify-self-end flex gap-3">
              <div className="hidden md:block">
                <CTAButton href="/hire">Get in touch</CTAButton>
              </div>
              <button
                className="md:hidden h-10 w-10 inline-flex items-center justify-center"
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </Container>

        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-50 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div
                className="absolute inset-0 bg-[#0c1110]"
                onClick={() => setOpen(false)}
              />
              <motion.div
                ref={panelRef}
                className="relative z-10 flex h-full flex-col"
                initial={{ y: -24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -24, opacity: 0 }}
              >
                <nav className="mt-8 px-6">
                  {[
                    ["Services", "/services"],
                    ["The team", "/team"],
                    ["Join us", "/join"],
                    ["Hire us", "/hire"],
                    ["About", "/about"],
                  ].map(([label, to]) => (
                    <NavLink
                      key={to}
                      to={to}
                      onClick={() => setOpen(false)}
                      className="block py-4 text-2xl border-b border-white/10"
                    >
                      {label}
                    </NavLink>
                  ))}
                </nav>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-white/10 py-16">
        <Container>
          <BlockLogo />
          <p className="mt-6 text-sm text-white/50">
            © {new Date().getFullYear()} Boldbrave AB. All rights reserved.{" "}
            <Link to="/privacy" className="hover:text-white underline">
              Privacy policy
            </Link>
          </p>
        </Container>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Startpage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="join" element={<JoinPage />} />
          <Route path="hire" element={<HirePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/platforms" element={<ServicesPlatformPage />} />
          <Route path="services/apps" element={<ServicesWebappsPage />} />
          <Route path="services/startups" element={<ServicesStartupsPage />} />
          <Route
            path="services/advisory"
            element={<ServicesAdvisoryChangePage />}
          />
          <Route path="about" element={<AboutPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="cv" element={<CVIndexPage />} />

          <Route
            path="*"
            element={<div className="p-8 text-center">Not found</div>}
          />
        </Route>

        {/* CV Routes - without header/footer layout */}
        <Route path="cv/johan-rosengren" element={<CVJohanRosengren />} />
        <Route path="cv/marie-coletti" element={<CVMarieColetti />} />
        <Route
          path="cv/magnus-christiansson"
          element={<CVMagnusChristiansson />}
        />
        <Route path="cv/philip-persson" element={<CVPhilipPersson />} />
        <Route path="cv/patrik-rizell" element={<CVPatrikRizell />} />
        <Route path="cv/viktor-nilsson" element={<CVViktorNilsson />} />
        <Route path="cv/patrik-pochill" element={<CVPatrikPochill />} />
        <Route path="cv/lyupcho-kotev" element={<CVLyupchoKotev />} />
        <Route path="cv/vincent-lam" element={<CVVincentLam />} />
        <Route path="cv/patrick-fogwall" element={<CVPatrickFogwall />} />
        <Route path="cv/johannes-tegner" element={<CVJohannesTegner />} />
        <Route path="cv/alexander-nyberg" element={<CVAlexanderNyberg />} />
        <Route path="cv/thomas-bergstrom" element={<CVThomasBergstrom />} />
        <Route path="cv/markus-rytterkull" element={<CVMarkusRytterkull />} />
        <Route path="cv/tomas-boremalm" element={<CVTomasBoremalm />} />
        <Route path="cv/nell-levasseur" element={<CVNellLevasseur />} />
        <Route path="cv/anders-soderlin" element={<CVAndersSoderlin />} />
        <Route path="cv/anton-alfsson" element={<CVAntonAlfsson />} />
        <Route path="cv/felix-tran" element={<CVFelixTran />} />
        <Route path="cv/joen-mauritzon" element={<CVJoenMauritzon />} />
        <Route
          path="cv/marcel-urrutia-nilsson"
          element={<CVMarcelUrrutiaNilsson />}
        />
        <Route path="cv/nils-broman" element={<CVNilsBroman />} />
        <Route path="cv/arun-rathi" element={<CVArunRathi />} />
        <Route path="cv/martin-levin" element={<CVMartinLevin />} />
        <Route
          path="cv/sebastian-andersson"
          element={<CVSebastianAndersson />}
        />
        <Route path="cv/william-phan" element={<CVWilliamPhan />} />
        <Route path="cv/jerry-hon" element={<CVJerryHon />} />
        <Route path="cv/vladislavs-gaidass" element={<CVVladislavsGaidass />} />
        <Route
          path="cv/carl-emil-kjellstrand"
          element={<CVCarlEmilKjellstrand />}
        />
        <Route path="cv/alexander-raunert" element={<CVAlexanderRaunert />} />
        <Route path="cv/alexander-madsen" element={<CVAlexanderMadsen />} />
      </Routes>
    </>
  );
}
